import useLocalDispatch from 'hooks/useLocalDispatch';
import { reactivateTeam } from 'thunk';
import { ChangeEvent, ComponentProps, useState, useRef, MouseEventHandler } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { checkIsApiCallFulfilled } from 'utils';
import { StyledDefaultPrimaryButton, StyledForm } from 'styles';
import { useNotification } from 'hooks/useNotification';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';

const initialState = {
  teamId: undefined,
};

type InitialStateType = {
  teamId?: number;
};

const stateId = 'reactivate-card';

export const ReactivateDiscardedTeamForm = () => {
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { showSuccess, showError } = useNotification();
  const { isRequesting, data } = selectLocalData({
    stateId,
  });
  const [{ teamId }, setTeamManagementState] = useState<InitialStateType>(initialState);

  // Tenant/realm selection (shared dropdowns). These provide context
  const { selectedTenant, selectedRealm, onSelectTenantRealm, onSelectRealm } =
    useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };
  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({ target: e.currentTarget, tenantId: selectedTenant.id });
    }
  };

  const onReactivateDiscardedTeamClick: React.MouseEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    // Validate tenant selection for context (team must exist in this tenant)
    if (!selectedTenant) {
      showError('Please select a tenant to verify team context');
      return;
    }

    if (teamId) {
      dispatchLocal(reactivateTeam, {
        params: {
          teamIds: [+teamId],
          tenantName: selectedTenant?.tenantName || undefined,
          realmId: selectedRealm?.realm_id || undefined,
        },
        stateId,
        onSuccessCallback: ({ response }) => {
          const { status } = response[0];

          if (checkIsApiCallFulfilled(status)) {
            showSuccess(`Team ${teamId} is reactivated successfully`);
          } else {
            showError(`Reactivating Team ${teamId} failed`);
          }
        },
        onFailureCallback: ({ error }: any) => {
          const apiMessage = error?.data?.message || error?.message;
          const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
          showError(`Reactivating Team ${teamId} failed${apiMessageSuffix}`);
        },
      });
    }
  };

  const onTeamIdToReactivateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTeamManagementState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <h3>Re-Activate Discarded Team</h3>
      <StyledForm onSubmit={onReactivateDiscardedTeamClick}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Organization
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {selectedTenant ? (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant.id} />
              ) : (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={1} />
              )}
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={selectedRealm?.realm_id || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>
          <TextField
            label="Mosaic Team ID"
            name="teamId"
            value={teamId}
            type="number"
            onChange={onTeamIdToReactivateChange}
            required
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            mt: 6,
          }}
        >
          <StyledDefaultPrimaryButton variant="outlined" type="submit" disabled={isRequesting}>
            Request
          </StyledDefaultPrimaryButton>
        </Box>
      </StyledForm>
    </>
  );
};
